/**
 * useUserPropertiesRefresh — pulls live state from app stores and re-sets
 * Firebase Analytics user properties so cohort segments stay accurate.
 *
 * Why: setUserProperties at signup only is frozen-in-time. After a few weeks
 * of usage, the user's actual state (subscription_tier, portfolios_count,
 * watchlist_count, level_up_tier, features_used_count) diverges from what
 * Analytics knows. This hook keeps them in sync.
 *
 * Runs on:
 *   1. Initial mount (once per session)
 *   2. When any of the watched store slices change
 *
 * The hook does NOT block render or fire events — only sets user properties
 * via the existing analytics.setUserProperties path.
 */
import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { useSavedPortfolioStore } from '../stores/savedPortfolioStore';
import { useWatchlistStore } from '../stores/watchlistStore';
import { useLevelUpStore } from '../stores/levelUpStore';
import analyticsService from '../services/analytics';

/**
 * Map the (webhook-synced) subscription `plan` to the canonical Analytics tier.
 *
 * Subscription plans: 'free' | 'signals' | 'strategist'
 * Analytics tiers:     'starter' | 'investor' | 'team' | 'enterprise'
 *
 * Legacy plan names alias to the same analytics tier so historical data stays
 * comparable. One place to update if the analytics tier names ever change.
 */
function planToTier(plan: string | undefined): string {
  switch (plan) {
    case 'signals':
    case 'premium':      // legacy alias
      return 'investor';
    case 'strategist':
    case 'enterprise':   // legacy alias
      return 'team';
    case 'free':
    default:
      return 'starter';
  }
}

export function useUserPropertiesRefresh() {
  const { user } = useAuth();
  const plan = useSubscriptionStore(s => s.plan);
  const portfolios = useSavedPortfolioStore(s => s.portfolios);
  const watchlists = useWatchlistStore(s => s.watchlists);
  const levelUpTier = useLevelUpStore(s => s.levelProgress.currentLevel);

  // Depend on the arrays themselves (not just .length) so content changes —
  // e.g. holdings edited within an existing portfolio, or symbols added to
  // an existing watchlist — also trigger a refresh. Zustand returns new
  // array references on mutation, so this is cheap and correct.
  useEffect(() => {
    if (!user) return; // Unauthenticated — no user-property refresh

    const portfoliosCount = portfolios.length;
    const watchlistsCount = watchlists.length;
    const watchlistSymbolsTotal = watchlists.reduce(
      (sum, w) => sum + (w.items?.length || 0), 0,
    );
    const tier = planToTier(plan);

    // Power-user cohort heuristic — mirrors the cohort definition in the
    // analytics audit. Centralised here so changes propagate to every
    // cohort report at once.
    const isPowerUser = portfoliosCount >= 3
      || watchlistSymbolsTotal >= 10
      || (levelUpTier !== undefined && levelUpTier >= 3);

    // Keys are snake_case to match the GA4 user-property convention; the
    // refresh service no longer auto-transforms.
    analyticsService.refreshUserProperties({
      subscription_tier: tier,
      portfolios_count: portfoliosCount,
      watchlists_count: watchlistsCount,
      watchlist_symbols_total: watchlistSymbolsTotal,
      level_up_tier: levelUpTier,
      is_power_user: isPowerUser,
    });
  }, [user, plan, portfolios, watchlists, levelUpTier]);
}
